/** Le nom du compte, choisi par le client a la creation et modifiable ensuite
 *  par le proprietaire ou un administrateur ; le numero de compte ne change
 *  jamais (Franck, 07/10/2026). */
import { useEffect, useState } from "react";

import { api, type Moi } from "../api";
import { Erreur } from "../components/ui";

export function NomDuCompte() {
  const [moi, setMoi] = useState<Moi | null>(null);
  const [nom, setNom] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [fait, setFait] = useState(false);

  useEffect(() => {
    api.moi().then((m) => { setMoi(m); setNom(m.tenant?.name ?? ""); }, setErreur);
  }, []);

  if (!moi) return <Erreur erreur={erreur} />;
  const peutModifier = moi.role === "owner" || moi.role === "admin";
  const change = nom.trim() !== "" && nom.trim() !== (moi.tenant?.name ?? "");

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Votre compte</h2>
      <p style={{ margin: "0 0 0.75rem" }}>
        Numéro de compte : <strong>{moi.tenant?.numero ?? moi.tenant?.slug}</strong>{" "}
        <span className="muted">(attribué à la création, il ne change jamais)</span>
      </p>
      <form onSubmit={(e) => {
        e.preventDefault();
        if (!change || enCours) return;
        setEnCours(true);
        setErreur(null);
        setFait(false);
        api.renommerCompte(nom.trim())
          .then((r) => { setMoi({ ...moi, tenant: moi.tenant ? { ...moi.tenant, name: r.nom } : moi.tenant }); setFait(true); },
                setErreur)
          .finally(() => setEnCours(false));
      }}>
        <div className="field" style={{ maxWidth: "28rem" }}>
          <label className="field-label" htmlFor="nom-compte">Nom du compte</label>
          <input id="nom-compte" className="input" maxLength={200} value={nom} disabled={!peutModifier}
                 onChange={(e) => { setNom(e.target.value); setFait(false); }} />
          {!peutModifier && (
            <p className="muted" style={{ marginTop: "0.3rem" }}>
              Modifiable par le propriétaire ou un administrateur du compte.
            </p>
          )}
        </div>
        {peutModifier && (
          <div className="actions" style={{ marginTop: "0.5rem" }}>
            <button type="submit" className="btn btn--primary" disabled={!change || enCours}>
              {enCours ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        )}
      </form>
      <Erreur erreur={erreur} />
      {fait && <div className="alert alert--success" role="status" style={{ marginTop: "0.75rem" }}>
        Nom enregistré. Il apparaîtra au prochain rechargement de la page.</div>}
    </div>
  );
}

/** Lot 2ter (Franck, 19/10/2026) : le prenom et le nom de la personne
 *  connectee. Une fois sa cle de signature creee apres une verification
 *  d'identite, ils viennent de la piece verifiee : affiches en lecture seule,
 *  « vérifié » ; le serveur refuse de les modifier. */
export function IdentiteDuCompte() {
  const [moi, setMoi] = useState<Moi | null>(null);

  useEffect(() => {
    api.moi().then(setMoi, () => undefined);
  }, []);

  if (!moi || !(moi.given_name || moi.family_name)) return null;
  const verifie = Boolean(moi.nom_verifie_le);

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
        Votre identité {verifie && <span className="pill pill--ok">vérifié</span>}
      </h2>
      <div className="field-row" style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="field" style={{ flex: "1 1 12rem", maxWidth: "16rem" }}>
          <label className="field-label" htmlFor="identite-prenom">Prénom</label>
          <input id="identite-prenom" className="input" value={moi.given_name ?? ""} readOnly
                 style={{ background: "var(--bg)" }} aria-describedby="identite-aide" />
        </div>
        <div className="field" style={{ flex: "1 1 12rem", maxWidth: "16rem" }}>
          <label className="field-label" htmlFor="identite-nom">Nom</label>
          <input id="identite-nom" className="input" value={moi.family_name ?? ""} readOnly
                 style={{ background: "var(--bg)" }} aria-describedby="identite-aide" />
        </div>
      </div>
      <p className="muted" id="identite-aide" style={{ marginTop: "0.3rem" }}>
        {verifie
          ? `Nom vérifié sur votre pièce d'identité le ${new Date(moi.nom_verifie_le as string).toLocaleDateString("fr-FR")} : il ne peut plus être modifié.`
          : "Le nom indiqué à la création de votre compte."}
      </p>
    </div>
  );
}
